<template>
  <div>
    <form class="conversation--form custom-scroll" @keydown="handleFormKeydown">
      <div>
        <div class="row">
          <div class="columns">
            <div class="w-full mb-4">
              <label
                class="text-gray-700 text-sm leading-5 mb-1.5"
                :class="{ error: $v.subject.$error }"
              >
                {{ $t('NEW_CONVERSATION.FORM.SUBJECT.LABEL') }}
              </label>
              <input
                v-model="subject"
                type="text"
                class="rounded-[8px] h-[44px] px-3 py-[10px] mb-0"
                :placeholder="$t('NEW_CONVERSATION.FORM.SUBJECT.PLACEHOLDER')"
                :class="
                  $v.subject.$invalid
                    ? 'border-[#B91C1C] hover:border-[#B91C1C] focus:border-[#B91C1C]'
                    : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                "
                @input="$v.subject.$touch"
              />
              <span v-if="$v.subject.$error" class="text-[#B91C1C] text-sm">
                {{ $t('NEW_CONVERSATION.FORM.SUBJECT.ERROR') }}
              </span>
            </div>
            <div class="row mb-6">
              <label
                v-tooltip.top="$t('NEW_CONVERSATION.FORM.ASSIGNEE_ME_TOOLTIP')"
                class="flex items-center gap-2 w-fit"
              >
                <input
                  v-model="isAssigneeMe"
                  type="checkbox"
                  class="semantic-checkbox !mx-0"
                />
                <span class="text-sm text-gray-800 font-normal">
                  {{ $t('NEW_CONVERSATION.FORM.ASSIGNEE_ME_LABEL') }}
                </span>
              </label>
            </div>
            <div class="w-full">
              <div
                class="editor-wrap"
                :class="
                  $v.message.$error ? 'border-[#B91C1C]' : 'border-gray-200'
                "
              >
                <reply-email-head
                  :mode="forwardMode"
                  :to-emails.sync="toEmail"
                  :cc-emails.sync="ccEmails"
                  :bcc-emails.sync="bccEmails"
                  :is-showing-reply-box="false"
                  :from-email="emailFromAddress"
                  @set-forward-contact="onSelectContact"
                />
                <email-woot-message-editor
                  ref="emailEditor"
                  v-model="message"
                  class="message-editor"
                  :inbox="targetInbox"
                  :class="{ editor_warning: $v.message.$error }"
                  :enable-variables="true"
                  :signature="signatureToApply"
                  :allow-signature="true"
                  :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
                  :update-selection-with="updateEditorSelectionWith"
                  :attached-files="attachedFiles"
                  :remove-attachment="removeAttachment"
                  :enabled-menu-options="emailEditorMenuOptions"
                  :show-emoji-picker="showEmojiPicker"
                  :toggle-emoji-picker="toggleEmojiPicker"
                  :hide-emoji-picker="hideEmojiPicker"
                  :use-html-mode="true"
                  @clear-selection="clearEditorSelection"
                  @blur="$v.message.$touch"
                >
                  <template #toolbar-left>
                    <div
                      class="flex h-8 hover:bg-gray-100 rounded-md cursor-pointer"
                    >
                      <file-upload
                        ref="uploadAttachment"
                        v-tooltip.top="
                          $t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')
                        "
                        input-id="forwardEmailAttachment"
                        :size="4096 * 4096"
                        :accept="allowedFileTypes"
                        :multiple="true"
                        :drop="true"
                        :drop-directory="false"
                        :data="{
                          direct_upload_url:
                            '/rails/active_storage/direct_uploads',
                          direct_upload: true,
                        }"
                        @input-file="
                          file =>
                            fileUploadHandler(file, { isMailboxFileUpload: true })
                        "
                      >
                        <span
                          :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                          class="material-symbols-outlined message-toolbar-item"
                        >
                          attach_file
                        </span>
                      </file-upload>
                    </div>
                    <div v-if="showDocalButton" class="flex">
                      <span
                        v-tooltip.top="
                          $t('INTEGRATION_SETTINGS.DOCAL.CTA_MODAL.TITLE')
                        "
                        :class="{ disabled: !isDocalContactValid }"
                        class="material-symbols-outlined message-toolbar-item"
                        @click="isDocalContactValid && toggleDocal()"
                      >
                        contract_edit
                      </span>
                      <docal-documents-modal
                        :show="showDocalModal"
                        :contact-email="toEmail"
                        :inbox-id="targetInbox.id"
                        @signing-link="onDocalSigningLink"
                        @cancel="showDocalModal = false"
                      />
                    </div>
                    <div v-if="showEncryptFilesButton" class="flex">
                      <span
                        v-tooltip.top="encryptFilesTooltip"
                        class="material-symbols-outlined message-toolbar-item"
                        :class="{ 'active encrypt-files-on': encryptFiles }"
                        @click="toggleEncryptFiles"
                      >
                        {{ encryptFiles ? 'lock' : 'lock_open_right' }}
                      </span>
                    </div>
                  </template>
                  <template
                    v-if="isMessageLengthReachingThreshold"
                    #warning-message
                  >
                    <div class="text-xs px-4 py-1 text-right">
                      <span :class="charLengthClass">
                        {{ `${message.length}/${maxLength}` }}
                      </span>
                    </div>
                  </template>
                </email-woot-message-editor>
              </div>
              <span v-if="$v.message.$error" class="text-[#B91C1C] text-sm">
                {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </form>
    <div
      class="flex flex-row justify-end gap-2 pt-4 pb-6 px-6 w-full z-[60] relative border-t border-gray-100"
      style="box-shadow: 0px -1px 4px 0px #cacaca29"
    >
      <span
        v-if="showEncryptFilesButton && encryptFiles"
        class="encrypt-files-chip ltr:mr-auto rtl:ml-auto"
      >
        <span class="material-symbols-outlined text-[16px]/[16px]">lock</span>
        {{ $t('CONVERSATION.REPLYBOX.ENCRYPT_FILES_ACTIVE') }}
      </span>
      <woot-button
        class="cancel-filter-btn button clear text-base px-5 py-3 leading-[20px] h-[48px] font-[400]"
        color-scheme="secondary"
        @click.prevent="onCancel"
      >
        {{ $t('NEW_CONVERSATION.FORM.CANCEL') }}
      </woot-button>
      <woot-submit-button
        :disabled="!isAbleToSendMsg"
        :button-text="$t('NEW_CONVERSATION.FORM.SUBMIT')"
        :loading="conversationsUiFlags.isCreating"
        class="button-filled text-base px-5 py-3 font-[400] h-[48px]"
        @click.native.prevent="onFormSubmit"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants.js';
import EmailWootMessageEditor from 'dashboard/components/widgets/WootWriter/EmailEditor/IndexV2.vue';
import ReplyEmailHead from './ReplyEmailHead';
import FileUpload from 'vue-upload-component';
import { ALLOWED_FILE_TYPES, MESSAGE_TYPE } from 'shared/constants/messages';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { validEmailsByComma } from 'dashboard/components/widgets/conversation/helpers/emailHeadHelper';
import { appendSignature } from 'dashboard/helper/editorHelper';
import alertMixin from 'shared/mixins/alertMixin';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import DocalDocumentsModal from 'dashboard/components/widgets/conversation/DocalDocumentsModal.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import rtlMixin from 'shared/mixins/rtlMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import { EMAIL_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import { generateBotMessageContent } from '../../../../components/widgets/conversation/helpers/botMessageContentHelper';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import timeMixin from 'dashboard/mixins/time.js';

export default {
  name: 'ForwardEmailForm',
  components: {
    EmailWootMessageEditor,
    ReplyEmailHead,
    FileUpload,
    DocalDocumentsModal,
  },
  mixins: [
    alertMixin,
    fileUploadMixin,
    uiSettingsMixin,
    inboxMixin,
    rtlMixin,
    hasPermissionMixin,
    messageFormatterMixin,
    timeMixin,
  ],
  props: {
    forwardMessage: {
      type: Object,
      required: true,
    },
    inbox: {
      type: Object,
      required: true,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      contact: null,
      toEmail: '',
      contactName: '',
      subject: '',
      message: '',
      ccEmails: '',
      bccEmails: '',
      attachedFiles: [],
      forwardHtmlContent: '',
      isAssigneeMe: true,
      showDocalModal: false,
      encryptFiles: false,
      showEmojiPicker: false,
      updateEditorSelectionWith: '',
      forwardMode: REPLY_EDITOR_MODES.FORWARD,
      emailEditorMenuOptions: EMAIL_EDITOR_MENU_OPTIONS,
      quoteLimit: 50,
    };
  },
  validations: {
    subject: { required },
    message: { required },
    toEmail: {
      required,
      hasValidEmail(value) {
        return validEmailsByComma(value || '');
      },
    },
  },
  computed: {
    ...mapGetters({
      conversationsUiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
      globalConfig: 'globalConfig/get',
      getMessageSignature: 'getMessageSignature',
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    showDocalButton() {
      if (!this.account?.additional_attributes?.document_signature_enabled) {
        return false;
      }

      return this.isAnEmailChannel || this.isASmsInbox;
    },
    showEncryptFilesButton() {
      return this.globalConfig.analystShowEncrypt && this.isAnEmailChannel;
    },
    encryptFilesTooltip() {
      return this.encryptFiles
        ? this.$t('CONVERSATION.REPLYBOX.TIP_ENCRYPT_FILES_ON')
        : this.$t('CONVERSATION.REPLYBOX.TIP_ENCRYPT_FILES_OFF');
    },
    isDocalEmailValid() {
      const email = this.toEmail;
      return email && validEmailsByComma(email);
    },
    isDocalContactValid() {
      if (this.isAnEmailChannel) return this.isDocalEmailValid;
      return false;
    },
    targetInbox() {
      return this.inbox || {};
    },
    messageSignature() {
      return this.getMessageSignature(this.targetInbox.id)?.body || '';
    },
    signatureToApply() {
      return this.messageSignature;
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    maxLength() {
      return MESSAGE_MAX_LENGTH.EMAIL;
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charLengthClass() {
      return this.message.length > this.maxLength
        ? 'text-red-600'
        : 'text-slate-600';
    },
    isAbleToSendMsg() {
      if (
        this.$v.message.$error ||
        this.$v.subject.$error ||
        !this.toEmail?.length ||
        this.message.length > this.maxLength
      )
        return false;

      return !!this.targetInbox.id;
    },
    forwardPayload() {
      const payload = {
        inboxId: this.targetInbox.id,
        contact: {
          email: this.toEmail,
          name: this.contactName || this.toEmail,
          phone_number: undefined,
        },
        message: {
          content: this.message,
          forwarded: true,
          to_emails: this.toEmail,
          forward_html_content: this.message,
        },
        mailSubject: this.subject,
        newConversation: true,
        assigneeId: this.isAssigneeMe ? this.currentUser.id : undefined,
        originalConversationId: this.forwardMessage?.conversation_id,
      };

      if (this.ccEmails) {
        payload.message.cc_emails = this.ccEmails;
      }
      if (this.bccEmails) {
        payload.message.bcc_emails = this.bccEmails;
      }
      if (this.showEncryptFilesButton && this.encryptFiles) {
        payload.message.encrypt_files = true;
      }
      if (this.attachedFiles && this.attachedFiles.length) {
        payload.files = [];
        this.setAttachmentPayload(payload);
      }

      return payload;
    },
    emailFromAddress() {
      return this.inbox?.email || '';
    },
    messageContent() {
      const contentAttributes = this.forwardMessage.content_attributes || {};

      if (contentAttributes.forwarded) {
        return (
          contentAttributes.forward_html_content || this.forwardMessage.content
        );
      }

      const botMessageContent = generateBotMessageContent(
        this.forwardMessage.content_type,
        contentAttributes,
        {
          noResponseText: this.$t('CONVERSATION.NO_RESPONSE'),
          csat: {
            ratingTitle: this.$t('CONVERSATION.RATING_TITLE'),
            feedbackTitle: this.$t('CONVERSATION.FEEDBACK_TITLE'),
          },
        }
      );

      if (this.forwardMessage.content_type === 'input_csat') {
        return this.$t('CONVERSATION.CSAT_REPLY_MESSAGE') + botMessageContent;
      }

      return (
        this.formatMessage(
          this.forwardMessage.content,
          false,
          this.forwardMessage.private,
          false
        ) + botMessageContent
      );
    },
  },
  created() {
    this.prefillFromForwardMessage();
  },
  mounted() {
    if (this.messageSignature) {
      this.message = appendSignature(this.message, this.signatureToApply);
    }
    this.$nextTick(() => {
      if (this.forwardHtmlContent && this.$refs.emailEditor) {
        this.$refs.emailEditor.appendHtmlBlock(this.forwardHtmlContent);
      }
    });
    this.appendQuotedHistory();
  },
  methods: {
    async appendQuotedHistory() {
      if (!this.forwardMessage?.conversation_id || !this.forwardMessage?.id) {
        return;
      }

      const quoteMessages = await this.$store.dispatch(
        'getForwardQuoteMessages',
        {
          conversationId: this.forwardMessage.conversation_id,
          messageId: this.forwardMessage.id,
          limit: this.quoteLimit,
        }
      );

      if (!quoteMessages || !quoteMessages.length) return;

      const quoteHtml = this.buildQuoteHtml(quoteMessages);
      this.$nextTick(() => {
        if (quoteHtml && this.$refs.emailEditor) {
          this.$refs.emailEditor.appendHtmlBlock(quoteHtml);
        }
      });
    },
    buildQuoteHtml(quoteMessages) {
      const dir = this.isRTLView ? 'rtl' : 'ltr';
      const align = this.isRTLView ? 'right' : 'left';
      const pStyle = `text-align: ${align}; direction: ${dir}`;
      const blockquoteStyle =
        'margin: 0 0 0 0.8ex; border-left: 1px solid #ccc; padding-left: 1ex';

      // Nest oldest-innermost, newest-outermost, mirroring how email
      // clients thread quoted replies.
      return quoteMessages.reduce((innerHtml, quoteMessage) => {
        const from =
          quoteMessage.sender?.from_original ||
          quoteMessage.sender?.email ||
          quoteMessage.sender?.name ||
          '';
        const date = quoteMessage.created_at
          ? this.messageStampFull(
              new Date(quoteMessage.created_at * 1000),
              'dd/MM/yyyy, h:mm:ss a'
            )
          : '';
        const header = this.$t('FORWARD_EMAIL.QUOTE_HEADER', { date, from });
        const content =
          quoteMessage.html_content ||
          this.formatMessage(
            quoteMessage.content,
            false,
            quoteMessage.private,
            false
          );

        return (
          `<blockquote style="${blockquoteStyle}">` +
          `<p style="${pStyle}">${this.escapeHtml(header)}</p>` +
          (quoteMessage.forwarded
            ? content
            : this.stripProviderQuotes(content)) +
          innerHtml +
          `</blockquote>`
        );
      }, '');
    },
    prefillFromForwardMessage() {
      if (!this.forwardMessage) return;
      let { from, to, originalSubject, date } = this.contentAttributes(
        this.forwardMessage
      );

      if (!to) {
        to = this.inbox.email;
      }
      if (!originalSubject) {
        originalSubject = `Fwd: ${this.currentChat?.additional_attributes?.mail_subject}`;
      }

      const emailAttrs = this.forwardMessage.content_attributes?.email || {};
      if (this.forwardMessage.content_attributes.forwarded === 'true') {
        this.forwardHtmlContent =
          emailAttrs.html_content?.full || this.messageContent || '';
      } else {
        this.forwardHtmlContent = this.stripProviderQuotes(
          emailAttrs.html_content?.full || this.messageContent || ''
        );
      }

      const subjectPrefix = originalSubject.toLowerCase().startsWith('fwd: ')
        ? ''
        : 'Fwd: ';
      this.subject = originalSubject
        ? `${subjectPrefix}${originalSubject}`
        : 'Fwd: ';

      const dir = this.isRTLView ? 'rtl' : 'ltr';
      const align = this.isRTLView ? 'right' : 'left';
      const pStyle = `text-align: ${align}; direction: ${dir}`;
      const line = text => `<p style="${pStyle}">${this.escapeHtml(text)}</p>`;

      const emptyLines = '<p><br></p>'.repeat(3);
      const forwardTitle = this.$t('FORWARD_EMAIL.FORWARD_MESSAGE_TITLE');
      const forwardHeader =
        line(`---------- ${forwardTitle} ----------`) +
        line(this.$t('FORWARD_EMAIL.FROM', { value: from })) +
        line(this.$t('FORWARD_EMAIL.DATE', { value: date })) +
        line(this.$t('FORWARD_EMAIL.SUBJECT', { value: originalSubject })) +
        line(this.$t('FORWARD_EMAIL.TO', { value: to }));

      this.message = `${emptyLines}${forwardHeader}`;

      this.attachedFiles = (this.forwardMessage.attachments || []).map(att => {
        const fileTypeMap = {
          image: 'image/jpeg',
          video: 'video/mp4',
          audio: 'audio/mpeg',
        };
        const contentType =
          att.content_type ||
          fileTypeMap[att.file_type] ||
          'application/octet-stream';
        const filename =
          att.filename ||
          decodeURIComponent(
            (att.data_url || '').split('/').pop().split('?')[0]
          ) ||
          'attachment';
        return {
          resource: {
            ...att,
            content_type: contentType,
            filename,
            byte_size: att.file_size,
          },
          thumb: att.thumb_url || att.data_url,
          blobSignedId: undefined,
          isPrefilledForward: true,
          dataUrl: att.data_url,
        };
      });
    },
    contentAttributes(message) {
      const emailAttrs = message.content_attributes?.email || {};
      const date = message.created_at
        ? this.messageStampFull(
            new Date(message.created_at * 1000),
            'dd/MM/yyyy, h:mm:ss a'
          )
        : '';
      const originalSubject = emailAttrs.subject || '';

      if (message.message_type === MESSAGE_TYPE.OUTGOING) {
        const currentContact = this.$store.getters['contacts/getContact'](
          this.currentChat?.meta?.sender?.id
        );
        const attrs = message.content_attributes;
        const toEmails = attrs?.to_emails?.length
          ? attrs?.to_emails
          : [currentContact?.email];
        return {
          from: this.targetInbox.email,
          to: Array.isArray(toEmails) ? toEmails.join(', ') : toEmails || '',
          originalSubject,
          date,
        };
      }

      const to = Array.isArray(emailAttrs.to)
        ? emailAttrs.to.join(', ')
        : emailAttrs.to || '';

      return {
        from: emailAttrs.from?.join(',') || '',
        to: to,
        originalSubject,
        date,
      };
    },
    escapeHtml(text) {
      if (!text) return '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    // Strips quoted history that email providers (Gmail, Outlook, Yahoo...)
    // embed inside the forwarded message's own HTML, since appendQuotedHistory
    // now rebuilds that history from Chatwoot's own conversation messages.
    stripProviderQuotes(html) {
      if (!html) return html;

      const container = document.createElement('div');
      container.innerHTML = html;

      const quoteSelectors = [
        'blockquote',
        '.gmail_quote',
        '[class*="gmail_quote"]',
        'div[class*="gmail_extra"]',
        'blockquote[type="cite"]',
        'blockquote[cite]',
        '[id*="yahoo_quoted"]',
        'blockquote[class*="yahoo_quoted"]',
        '[id*="mail-editor-reference-message-container"]',
        '[id*="divRplyFwdMsg"]',
      ];
      container
        .querySelectorAll(quoteSelectors.join(','))
        .forEach(el => el.remove());

      // Outlook desktop marks the start of quoted history with a plain
      // "border-top" divider (no semantic wrapper) instead of a blockquote;
      // everything from that divider onward is quoted content.
      const outlookDividerSelector = [
        'div[style*="border-top: solid #e1e1e1"]',
        'div[style*="border-top: solid #b5c4df"]',
        'div[style*="border-top:solid #E1E1E1"]',
        'div[style*="border-top:solid #B5C4DF"]',
        'div[style*="border-top: 1pt solid"]',
        'div[style*="border-top:1pt solid"]',
      ].join(',');
      const divider = container.querySelector(outlookDividerSelector);
      if (divider?.querySelector('b')) {
        let node = divider;
        while (node) {
          const next = node.nextSibling;
          node.remove();
          node = next;
        }
      }

      return container.innerHTML;
    },
    setAttachmentPayload(payload) {
      this.attachedFiles.forEach(attachment => {
        if (attachment.isPrefilledForward) {
          if (attachment.dataUrl) {
            payload.files.push(attachment.dataUrl);
          }
        } else if (this.globalConfig.directUploadsEnabled) {
          payload.files.push(attachment.blobSignedId);
        } else {
          payload.files.push(attachment.resource.file);
        }
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          resource: blob || file,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    handleFormKeydown(event) {
      if (event.key === 'Enter' && document.activeElement.type !== 'submit') {
        event.preventDefault();
      }
    },
    onCancel() {
      this.$emit('cancel');
    },
    onSelectContact(contact) {
      this.contact = contact;
      this.toEmail = contact.email;
      this.contactName = contact.name;
    },
    setEmail(value) {
      this.toEmail = value;
    },
    onFormSubmit() {
      this.createConversation(this.forwardPayload);
    },
    async createConversation(payload) {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        const data = await this.onSubmit({
          params: payload,
          isFromWhatsApp: false,
        });
        this.$emit('forward-sent', {
          newConversationId: data.id,
          recipients: this.toEmail,
        });
        this.$router.push({
          name: 'inbox_conversation',
          params: { conversation_id: data.id },
        });
      } catch (error) {
        if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      }
    },
    toggleDocal() {
      this.showDocalModal = !this.showDocalModal;
    },
    toggleEncryptFiles() {
      this.encryptFiles = !this.encryptFiles;
    },
    onDocalSigningLink(url) {
      this.showDocalModal = false;
      const currentMsg = this.message || '';
      const separator = currentMsg.trim() ? ' ' : '';
      this.message = currentMsg + separator + url;
    },
    emojiOnClick(emoji) {
      this.updateEditorSelectionWith = emoji;
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.conversation--form {
  padding: 16px 24px;
  max-height: 650px;
  overflow: auto;

  .file-uploads {
    text-align: start;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

.editor-wrap {
  @apply relative border bg-white rounded-lg mb-0;
}

.encrypt-files-on {
  @apply bg-green-50;

  color: #22a06b !important;
}

.encrypt-files-chip {
  @apply flex items-center gap-1 h-8 px-2 rounded-md bg-green-50 text-green-700 text-xs whitespace-nowrap select-none;
}

::v-deep .ProseMirror.ProseMirror-woot-style {
  min-height: 13.5rem;
}
</style>
